/**
 * TenantsScreen — Organisation > Tenants (CC 0111, operator goal 2026-09-30).
 *
 * Data: partial — ops: listNodes (root + children, live), getBranding via
 * loadTenantChrome (live), getTenantProfile (card 4; CC is building it). Until that
 * read answers, card 4 says "Not available yet": never sample values. A null field in
 * its answer reads "Not recorded".
 *
 * getTenantProfile is a settings.read operation: card 4 starts it only for a
 * signed-in user who holds settings.read, on a confirmed tenant, and drops an answer
 * that lands after the tenant, user or capability changed (tenantProfileAccess in
 * lib/tenant-modules). Without settings.read the card says "Not available yet".
 *
 * The list is always ONE row: the signed-in tenant. The screen never shows another
 * tenant. Read-only; identity and units are still edited in the node tree.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Btn, Card, Icon, PageState, T } from "@v0y4ge/jrni-ui";
import { getTenantProfile } from "@v0y4ge/api-client";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { onTenantChange, onTenantSwitching, subscribeAccess } from "../../shell/access";
import { loadTenantChrome, type TenantChrome } from "../../lib/branded-chrome";
import { studioRequestOptions } from "../../lib/studio-config";
import { tenantProfileAccess } from "../../lib/tenant-modules";
import { useNodeTree } from "./useNodes";
import type { UINode } from "./nodes-adapter";

type Profile = Awaited<ReturnType<typeof getTenantProfile>>;
type ProfileState = { kind: "loading" } | { kind: "ready"; p: Profile } | { kind: "unavailable" };

const NOT_RECORDED = "Not recorded";

/** "denied", "waiting", or the scope an answer must still match when it lands. */
function profileKey(): string {
  const access = tenantProfileAccess();
  return access.kind === "allowed" ? access.scope : access.kind;
}

/** A profile answer, tagged with the access scope it was read under. */
type OwnedProfile = { scope: string; st: ProfileState };
const PROFILE_LOADING: ProfileState = { kind: "loading" };
const PROFILE_UNAVAILABLE: ProfileState = { kind: "unavailable" };

function useTenantProfile(): ProfileState {
  const [key, setKey] = useState(profileKey);
  const [owned, setOwned] = useState<OwnedProfile | null>(null);
  useEffect(() => {
    const sync = () => setKey(profileKey());
    const offs = [subscribeAccess(sync), onTenantSwitching(sync), onTenantChange(sync)];
    sync();
    return () => { offs.forEach((off) => off()); };
  }, []);
  useEffect(() => {
    // Re-read access at the call site: start the read only while it is allowed for the scope this run owns.
    const access = tenantProfileAccess();
    if (access.kind !== "allowed" || access.scope !== key) { setKey(profileKey()); return; }
    let live = true;
    const current = () => live && profileKey() === key;
    let request: Promise<unknown>;
    try { request = getTenantProfile(studioRequestOptions("nodes.tenants") as never) as Promise<unknown>; }
    catch (e) { request = Promise.reject(e); }
    request
      .then((p) => { if (current()) setOwned({ scope: key, st: { kind: "ready", p: ((p as { data?: Profile })?.data ?? p) as Profile } }); })
      .catch(() => { if (current()) setOwned({ scope: key, st: PROFILE_UNAVAILABLE }); });
    return () => { live = false; };
  }, [key]);
  // Decide from the access in force NOW, so an answer read under another scope never renders.
  const now = profileKey();
  if (now === "denied") return PROFILE_UNAVAILABLE;
  if (now !== "waiting" && owned && owned.scope === now) return owned.st;
  return PROFILE_LOADING;
}

function useBrand(): TenantChrome | null {
  const [c, setC] = useState<TenantChrome | null>(null);
  useEffect(() => { let live = true; loadTenantChrome(true).then((x) => { if (live) setC(x); }).catch(() => {}); return () => { live = false; }; }, []);
  return c;
}

function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
}
const str = (v: unknown): string => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "");

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px minmax(0,1fr)", gap: 16, padding: "10px 0", borderTop: `1px solid ${T.border}`, fontSize: 13 }}>
      <span style={{ color: T.soft }}>{k}</span>
      <span style={{ color: v ? T.n1 : T.soft, minWidth: 0, overflowWrap: "anywhere" }}>{v || "—"}</span>
    </div>
  );
}
function CardTitle({ icon, title, desc }: { icon: ReactNode; title: string; desc: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
      <span style={{ width: 32, height: 32, borderRadius: 8, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{title}</div>
        <div style={{ fontSize: 12, color: T.soft }}>{desc}</div>
      </div>
    </div>
  );
}

export default function TenantsScreen() {
  const nav = useNav();
  const params = nav.params as { id?: string };
  const { nodes, loading, error, reload } = useNodeTree();
  const brand = useBrand();
  const root: UINode | null = nodes.find((n) => n.depth === 0 && !n.parent_id) ?? nodes.find((n) => !n.parent_id) ?? null;
  const units = root ? nodes.filter((n) => n.parent_id === root.id).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)) : [];
  const open = !!params.id && !!root && params.id === root.id;
  const color = brand?.brandColor || T.p1;

  return (
    <Screen>
      <PageHeader
        title={open && root ? root.name : "Tenants"}
        subtitle={open ? "The signed-in tenant's identity, units, brand and deployment." : "The tenant this Studio belongs to."}
        actions={<div style={{ display: "flex", gap: 8 }}>
          {open ? <Btn variant="secondary" onClick={() => nav.go("nodes.tenants")}>All tenants</Btn> : null}
          <Btn variant="secondary" onClick={() => nav.go("nodes")}>Organisation structure</Btn>
        </div>}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "8px 24px 40px" }}>
        <PageState noun="tenants" icon={<Icon.network size={20} />} loading={loading} error={error} onRetry={reload} empty={!loading && !error && !root} emptyTitle="No tenant root" emptyBody="The organisation tree has no root node yet.">
          {root && !open ? (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              {/* ui-raw: one clickable list row — a table of one */}
              <button type="button" onClick={() => nav.go("nodes.tenants", { id: root.id })} data-testid="tenant-row"
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", border: "none", background: T.white, cursor: "pointer", textAlign: "left", fontFamily: T.font }}>
                {brand?.logoUrl
                  ? <img src={brand.logoUrl} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain", background: T.n6 }} />
                  : <span style={{ width: 36, height: 36, borderRadius: 8, background: color, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>{initialsOf(root.name)}</span>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{root.name}</div>
                  <div style={{ fontSize: 12, color: T.soft }}>{[str(root.settings.industry), `${units.length} unit${units.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}</div>
                </div>
                <span style={{ width: 14, height: 14, borderRadius: 4, background: color, flexShrink: 0 }} title={`Brand colour ${color}`} />
                <span style={{ color: T.soft, display: "inline-flex" }}><Icon.chev size={14} dir="right" /></span>
              </button>
            </Card>
          ) : null}

          {root && open ? <TenantProfile root={root} units={units} brand={brand} onUnit={(id) => nav.go("nodes", { node: id })} /> : null}
        </PageState>
      </div>
    </Screen>
  );
}

function TenantProfile({ root, units, brand, onUnit }: { root: UINode; units: UINode[]; brand: TenantChrome | null; onUnit: (id: string) => void }) {
  const profile = useTenantProfile();
  const address = [root.address_line1, root.address_line2, root.city, root.postcode].filter(Boolean).join(", ");
  const color = brand?.brandColor || T.p1;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16, alignItems: "start" }}>
      <Card style={{ padding: 18 }}>
        <CardTitle icon={<Icon.info size={16} />} title="Identity and defaults" desc="From the tenant's root node." />
        <Row k="Name" v={root.name} />
        <Row k="Code" v={root.code} />
        <Row k="Industry" v={str(root.settings.industry)} />
        <Row k="Tagline" v={str(root.settings.tagline)} />
        <Row k="Founded" v={str(root.settings.founded ?? root.settings.founded_year)} />
        <Row k="Time zone" v={root.timezone} />
        <Row k="Locale" v={root.locale} />
        <Row k="Currency" v={root.currency} />
        <Row k="Country" v={root.country_code} />
        <Row k="Address" v={address} />
        <Row k="Phone" v={root.phone} />
        <Row k="Email" v={root.email} />
        <Row k="Website" v={root.website} />
      </Card>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ padding: 18 }}>
          <CardTitle icon={<Icon.users size={16} />} title="Units" desc="The root's direct children. Open one in the Organisation tree." />
          {units.length ? units.map((u) => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: `1px solid ${T.border}` }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: T.n1 }}>{u.name}<span style={{ color: T.soft }}>{u.kind ? ` · ${u.kind}` : ""}</span></span>
              <Btn size="sm" variant="secondary" onClick={() => onUnit(u.id)}>Open</Btn>
            </div>
          )) : <div style={{ fontSize: 13, color: T.soft, padding: "10px 0", borderTop: `1px solid ${T.border}` }}>No units under the root yet.</div>}
        </Card>

        <Card style={{ padding: 18 }}>
          <CardTitle icon={<Icon.tags size={16} />} title="Brand" desc="From Settings > Branding." />
          <Row k="Colour" v={brand ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 14, height: 14, borderRadius: 4, background: color }} />{color}</span> : ""} />
          <Row k="Logo" v={brand?.logoUrl ? <img src={brand.logoUrl} alt="" style={{ height: 28, maxWidth: 160, objectFit: "contain" }} /> : ""} />
          <Row k="Initials" v={initialsOf(root.name)} />
        </Card>

        <Card style={{ padding: 18 }}>
          <CardTitle icon={<Icon.network size={16} />} title="Deployment" desc="Where this tenant runs, and what is switched on." />
          {profile.kind === "loading" ? <div style={{ fontSize: 13, color: T.soft, padding: "10px 0", borderTop: `1px solid ${T.border}` }}>Loading…</div>
            : profile.kind === "unavailable" ? <div style={{ fontSize: 13, color: T.soft, padding: "10px 0", borderTop: `1px solid ${T.border}` }}>Not available yet.</div>
            : (() => {
                const p = profile.p;
                const pub = Object.entries(p.hosts.public || {});
                return <>
                  <Row k="Slug" v={p.slug} />
                  <Row k="Studio host" v={p.hosts.studio ?? NOT_RECORDED} />
                  <Row k="API host" v={p.hosts.api ?? NOT_RECORDED} />
                  <Row k="Public hosts" v={pub.length ? <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>{pub.map(([s, h]) => <span key={s}><span style={{ color: T.soft }}>{s}:</span> {h}</span>)}</span> : NOT_RECORDED} />
                  <Row k="Modules" v={p.modules ? (p.modules.length ? p.modules.join(", ") : "None") : NOT_RECORDED} />
                  <Row k="Version" v={p.deployment.versionId ?? NOT_RECORDED} />
                  <Row k="Deployed at" v={p.deployment.deployedAt ?? NOT_RECORDED} />
                </>;
              })()}
        </Card>
      </div>
    </div>
  );
}
